// src/components/page_components/AlertBanners.jsx
import {
  Stack,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";

const AlertBanners = () => {
  const { data, isLoading, isFetching } = useUserQuery();
  const navigate = useNavigate();
  const user = data?.user;

  if (isLoading || isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const isPersonalInformation = !!user?.personal_information;
  const isAuthorizationSigned = !!user?.authorization_signed;

  // ── Onboarding complete — show "Keep Your Information Current" CTA ──
  if (isPersonalInformation && isAuthorizationSigned) {
    return (
      <Box
        sx={{
          bgcolor: "#1A1F5E",
          borderRadius: 2,
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          position: "relative",
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 900 80"
            preserveAspectRatio="xMidYMid slice"
            fill="none"
          >
            <line
              x1="14"
              y1="12"
              x2="52"
              y2="12"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="3 5"
              strokeLinecap="round"
              opacity="0.45"
            />
            <line
              x1="20"
              y1="22"
              x2="46"
              y2="22"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="2 6"
              strokeLinecap="round"
              opacity="0.28"
            />
            <line
              x1="10"
              y1="32"
              x2="36"
              y2="32"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="3 4"
              strokeLinecap="round"
              opacity="0.18"
            />
            <line
              x1="16"
              y1="50"
              x2="50"
              y2="50"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="2 5"
              strokeLinecap="round"
              opacity="0.18"
            />
            <line
              x1="10"
              y1="60"
              x2="44"
              y2="60"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="3 5"
              strokeLinecap="round"
              opacity="0.35"
            />
            <line
              x1="18"
              y1="70"
              x2="42"
              y2="70"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="2 4"
              strokeLinecap="round"
              opacity="0.22"
            />
            <line
              x1="848"
              y1="10"
              x2="888"
              y2="10"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="3 6"
              strokeLinecap="round"
              opacity="0.28"
            />
            <line
              x1="854"
              y1="22"
              x2="886"
              y2="22"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="2 5"
              strokeLinecap="round"
              opacity="0.45"
            />
            <line
              x1="860"
              y1="32"
              x2="892"
              y2="32"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="4 5"
              strokeLinecap="round"
              opacity="0.18"
            />
            <line
              x1="850"
              y1="50"
              x2="888"
              y2="50"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="3 5"
              strokeLinecap="round"
              opacity="0.45"
            />
            <line
              x1="856"
              y1="60"
              x2="884"
              y2="60"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="2 6"
              strokeLinecap="round"
              opacity="0.22"
            />
            <line
              x1="848"
              y1="70"
              x2="878"
              y2="70"
              stroke="#f59e0b"
              strokeWidth="1"
              strokeDasharray="3 4"
              strokeLinecap="round"
              opacity="0.32"
            />
            <circle cx="6" cy="40" r="1.5" fill="#f59e0b" opacity="0.35" />
            <circle cx="894" cy="40" r="1.5" fill="#f59e0b" opacity="0.35" />
          </svg>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#FFFFFF",
              mb: 0.5,
            }}
          >
            Keep Your Information Current
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.78rem",
              color: "rgba(255,255,255,0.65)",
              maxWidth: 520,
              display: "block",
            }}
          >
            Accurate personal details ensure deletion requests are comprehensive
            and effective.
          </Typography>
        </Box>
        <Box
          onClick={() => navigate("/profile/personal-information")}
          sx={{
            px: 1.5,
            py: 0.75,
            bgcolor: "#f59e0b",
            borderRadius: 2,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            transition: "opacity 0.15s",
            flexShrink: 0,
            "&:hover": { opacity: 0.85 },
          }}
        >
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#000",
              whiteSpace: "nowrap",
            }}
          >
            Update Info →
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Onboarding incomplete — show warning alert ──
  const nextStep = isPersonalInformation
    ? "/profile/authorization"
    : "/profile/personal-information";

  const missingSteps = [];
  if (!isPersonalInformation) missingSteps.push("personal_info");
  if (!isAuthorizationSigned) missingSteps.push("authorization");

  const bothMissing = missingSteps.length === 2;

  return (
    <Stack spacing={1}>
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to={nextStep}
          >
            Continue
          </Button>
        }
      >
        <AlertTitle>Finish onboarding</AlertTitle>
        You need to complete your <strong>onboarding</strong> before we can
        start sending deletion requests on your behalf.{" "}
        {bothMissing && (
          <>
            This includes filling out your{" "}
            <strong>Personal Information Profile</strong> and signing the{" "}
            <strong>Authorization Form</strong>.
          </>
        )}
        {!isPersonalInformation && !bothMissing && (
          <>
            Please fill out your <strong>Personal Information Profile</strong>{" "}
            to continue.
          </>
        )}
        {!isAuthorizationSigned && !bothMissing && (
          <>
            Please sign the <strong>Authorization Form</strong> to continue.
          </>
        )}
      </Alert>
    </Stack>
  );
};

export default AlertBanners;
